import React from 'react';

import { useDroppable } from '@dnd-kit/core';

import { Chip, Paper, Stack, Typography } from '@mui/material';

import type { ProjectAssignee, ProjectTask, ProjectTaskStatus } from '~/services/ContractingService';

import DraggableTaskCard from './DraggableTaskCard';
import { TASK_STATUS_LABELS } from './projectManagementModel';

export interface DropIndicator {
  taskId: string;
  side: 'top' | 'bottom';
}

interface Props {
  dropTarget: { indicator?: DropIndicator } | null;
  status: ProjectTaskStatus;
  suggestedAssignees: Array<ProjectAssignee>;
  tasks: Array<ProjectTask>;
  onChangeAssignees: (taskId: string, assignees: Array<ProjectAssignee>) => void;
}

export default function TaskColumn({ dropTarget, status, suggestedAssignees, tasks, onChangeAssignees }: Props) {
  const { setNodeRef } = useDroppable({ id: status });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        bgcolor: dropTarget ? 'action.selected' : 'action.hover',
        borderColor: dropTarget ? 'primary.main' : undefined,
        minHeight: 320,
        p: 1.5,
        transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">{TASK_STATUS_LABELS[status]}</Typography>
        <Chip label={tasks.length} size="small" />
      </Stack>
      <Stack spacing={1}>
        {tasks.map((task) => (
          <DraggableTaskCard
            key={task.id}
            dropIndicator={dropTarget?.indicator?.taskId === task.id ? dropTarget.indicator.side : undefined}
            suggestedAssignees={suggestedAssignees}
            task={task}
            onChangeAssignees={onChangeAssignees}
          />
        ))}
      </Stack>
    </Paper>
  );
}
